import {
  NextScreenContext,
  PostAppointmentScreen,
  PostAppointmentScreenStepper,
} from '../context/PostAppointmentContext/PostAppointmentProvider'
import { AppointmentType } from '../types/types'

export const APPOINTMENT_CATEGORY = {
  INITIAL_MD: 'Initial MD',
  SECOND_MD: 'Second MD',
  FOLLOWUP_MD: 'Followup MD',
  CARE_TRANSFER_MD: 'Care Transfer MD',
  DIAGNOSTIC: 'Diagnostic',
  INITIAL_HMT: 'Initial HMT',
  INITIAL_RD: 'Initial RD',
  FOLLOWUP_RD: 'Followup RD',
  INITIAL_NUTRITION_ONLY: 'Initial Nutrition Only',
  FOLLOWUP_NUTRITION_ONLY: 'Followup Nutrition Only',
  INITIAL_ENDO: 'Initial Endo',
  FOLLOWUP_ENDO: 'Followup Endo',
  FOLLOWUP_ENDO_LONG: 'Followup Endo Long',
} as const

export const listScreens = (
  appointmentType: AppointmentType
): PostAppointmentScreenStepper[] => {
  switch (appointmentType.category) {
    case APPOINTMENT_CATEGORY.INITIAL_MD:
    case APPOINTMENT_CATEGORY.SECOND_MD:
    case APPOINTMENT_CATEGORY.FOLLOWUP_MD:
    case APPOINTMENT_CATEGORY.CARE_TRANSFER_MD:
    case APPOINTMENT_CATEGORY.INITIAL_ENDO:
    case APPOINTMENT_CATEGORY.FOLLOWUP_ENDO:
    case APPOINTMENT_CATEGORY.FOLLOWUP_ENDO_LONG:
      return mdStepper
    case APPOINTMENT_CATEGORY.DIAGNOSTIC:
      return dxStepper
    case APPOINTMENT_CATEGORY.INITIAL_HMT:
      return initialHMTStepper
    case APPOINTMENT_CATEGORY.INITIAL_RD:
    case APPOINTMENT_CATEGORY.FOLLOWUP_RD:
    case APPOINTMENT_CATEGORY.INITIAL_NUTRITION_ONLY:
    case APPOINTMENT_CATEGORY.FOLLOWUP_NUTRITION_ONLY:
    default:
      return rdStepper
  }
}

export const nextScreen = (
  currentScreen: PostAppointmentScreen,
  appointmentType: AppointmentType,
  context?: NextScreenContext
) => {
  switch (appointmentType.category) {
    case APPOINTMENT_CATEGORY.INITIAL_MD:
      return initialMDNextScreen(currentScreen, context)
    case APPOINTMENT_CATEGORY.SECOND_MD:
    case APPOINTMENT_CATEGORY.FOLLOWUP_MD:
    case APPOINTMENT_CATEGORY.CARE_TRANSFER_MD:
    case APPOINTMENT_CATEGORY.INITIAL_ENDO:
    case APPOINTMENT_CATEGORY.FOLLOWUP_ENDO:
    case APPOINTMENT_CATEGORY.FOLLOWUP_ENDO_LONG:
      return followupMDNextScreen(currentScreen, context)
    case APPOINTMENT_CATEGORY.DIAGNOSTIC:
      return diagnosticNextScreen(currentScreen)
    case APPOINTMENT_CATEGORY.INITIAL_HMT:
      return initialHMTNextScreen(currentScreen)
    case APPOINTMENT_CATEGORY.INITIAL_RD:
    case APPOINTMENT_CATEGORY.FOLLOWUP_RD:
    case APPOINTMENT_CATEGORY.INITIAL_NUTRITION_ONLY:
    case APPOINTMENT_CATEGORY.FOLLOWUP_NUTRITION_ONLY:
    default:
      return nutritionNextScreen(currentScreen)
  }
}

export const previousScreen = (
  currentScreen: PostAppointmentScreen,
  appointmentType: AppointmentType,
  context?: NextScreenContext
) => {
  switch (appointmentType.category) {
    case APPOINTMENT_CATEGORY.INITIAL_MD:
      return initialMDPreviousScreen(currentScreen, context)
    case APPOINTMENT_CATEGORY.SECOND_MD:
    case APPOINTMENT_CATEGORY.FOLLOWUP_MD:
    case APPOINTMENT_CATEGORY.CARE_TRANSFER_MD:
    case APPOINTMENT_CATEGORY.INITIAL_ENDO:
    case APPOINTMENT_CATEGORY.FOLLOWUP_ENDO:
    case APPOINTMENT_CATEGORY.FOLLOWUP_ENDO_LONG:
      return followupMDPreviousScreen(currentScreen, context)
    case APPOINTMENT_CATEGORY.DIAGNOSTIC:
      return diagnosticPreviousScreen(currentScreen)
    case APPOINTMENT_CATEGORY.INITIAL_HMT:
      return initialHMTPreviousScreen(currentScreen)
    case APPOINTMENT_CATEGORY.INITIAL_RD:
    case APPOINTMENT_CATEGORY.FOLLOWUP_RD:
    case APPOINTMENT_CATEGORY.INITIAL_NUTRITION_ONLY:
    case APPOINTMENT_CATEGORY.FOLLOWUP_NUTRITION_ONLY:
    default:
      return nutritionPreviousScreen(currentScreen)
  }
}

const initialMDNextScreen = (
  currentScreen: PostAppointmentScreen,
  context?: NextScreenContext
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'initial':
      return 'orders'
    case 'orders':
      return context?.noFollowUp ? 'note' : 'timeline'
    case 'timeline':
      return 'note'
    case 'note':
      return 'review'
    case 'review':
    default:
      return 'initial'
  }
}

const followupMDNextScreen = (
  currentScreen: PostAppointmentScreen,
  context?: NextScreenContext
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'initial':
      return 'orders'
    case 'orders':
      return context?.noFollowUp ? 'note' : 'timeline'
    case 'timeline':
      return 'note'
    case 'note':
      return 'review'
    case 'review':
    default:
      return 'initial'
  }
}

const initialHMTNextScreen = (
  currentScreen: PostAppointmentScreen
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'initial':
      return 'orders'
    case 'orders':
      return 'note'
    case 'note':
      return 'review'
    case 'review':
    default:
      return 'initial'
  }
}

const diagnosticNextScreen = (
  currentScreen: PostAppointmentScreen
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'initial':
      return 'orders'
    case 'orders':
      return 'recommended-program'
    case 'recommended-program':
      return 'note'
    case 'note':
      return 'review'
    case 'review':
    default:
      return 'initial'
  }
}

const nutritionNextScreen = (
  currentScreen: PostAppointmentScreen
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'initial':
      return 'note'
    case 'note':
      return 'review'
    case 'review':
    default:
      return 'initial'
  }
}

const initialMDPreviousScreen = (
  currentScreen: PostAppointmentScreen,
  context?: NextScreenContext
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'orders':
      return 'initial'
    case 'timeline':
      return 'orders'
    case 'note':
      return context?.moreTestsOrdered || context?.noFollowUp
        ? 'orders'
        : 'timeline'
    case 'review':
      return 'note'
    default:
      return 'initial'
  }
}

const followupMDPreviousScreen = (
  currentScreen: PostAppointmentScreen,
  context?: NextScreenContext
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'orders':
      return 'initial'
    case 'timeline':
      return 'orders'
    case 'note':
      return context?.moreTestsOrdered || context?.noFollowUp
        ? 'orders'
        : 'timeline'
    case 'review':
      return 'note'
    default:
      return 'initial'
  }
}

const initialHMTPreviousScreen = (
  currentScreen: PostAppointmentScreen
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'orders':
      return 'initial'
    case 'note':
      return 'orders'
    case 'review':
      return 'note'
    default:
      return 'initial'
  }
}

const diagnosticPreviousScreen = (
  currentScreen: PostAppointmentScreen
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'orders':
      return 'initial'
    case 'recommended-program':
      return 'orders'
    case 'note':
      return 'recommended-program'
    case 'review':
      return 'note'
    default:
      return 'initial'
  }
}

const nutritionPreviousScreen = (
  currentScreen: PostAppointmentScreen
): PostAppointmentScreen => {
  switch (currentScreen) {
    case 'note':
      return 'initial'
    case 'review':
      return 'note'
    default:
      return 'initial'
  }
}

/**
 * Editing a signed note replays the note's own wizard, minus the screens whose
 * fields no addendum op can write, and ends on the summary of changes instead
 * of the sign-off review.
 */
const EDITABLE_STEP_SCREENS: PostAppointmentScreen[] = [
  'orders',
  'timeline',
  'note',
]

export const editScreens = (
  appointmentType: AppointmentType
): PostAppointmentScreenStepper[] =>
  [
    // The follow-up step is never dropped from an edit: it is where a
    // withdrawal is recorded, so removing it would remove the control.
    ...listScreens(appointmentType).filter((step) =>
      EDITABLE_STEP_SCREENS.includes(step.screen)
    ),
    {
      index: 0,
      title: 'Review',
      screen: 'amend-review' as PostAppointmentScreen,
    },
  ].map((step, position) => ({ ...step, index: position + 1 }))

const stepAt = (
  currentScreen: PostAppointmentScreen,
  appointmentType: AppointmentType,
  offset: number
): PostAppointmentScreen | undefined => {
  const steps = editScreens(appointmentType)
  const position = steps.findIndex((step) => step.screen === currentScreen)

  if (position === -1) {
    return steps[0]?.screen
  }

  return steps[position + offset]?.screen
}

export const editNextScreen = (
  currentScreen: PostAppointmentScreen,
  appointmentType: AppointmentType
): PostAppointmentScreen =>
  stepAt(currentScreen, appointmentType, 1) ?? 'amend-review'

/** undefined means the first step, so the caller leaves the edit flow. */
export const editPreviousScreen = (
  currentScreen: PostAppointmentScreen,
  appointmentType: AppointmentType
): PostAppointmentScreen | undefined =>
  stepAt(currentScreen, appointmentType, -1)

const mdStepper: PostAppointmentScreenStepper[] = [
  {
    index: 1,
    title: 'Orders',
    screen: 'orders',
  },
  {
    index: 2,
    title: 'Timeline',
    screen: 'timeline',
  },
  {
    index: 3,
    title: 'Note',
    screen: 'note',
  },
  {
    index: 4,
    title: 'Review',
    screen: 'review',
  },
]

const rdStepper: PostAppointmentScreenStepper[] = [
  {
    index: 1,
    title: 'Note',
    screen: 'note',
  },
  {
    index: 2,
    title: 'Review',
    screen: 'review',
  },
]

const initialHMTStepper: PostAppointmentScreenStepper[] = [
  {
    index: 1,
    title: 'Orders',
    screen: 'orders',
  },
  {
    index: 2,
    title: 'Note',
    screen: 'note',
  },
  {
    index: 3,
    title: 'Review',
    screen: 'review',
  },
]

const dxStepper: PostAppointmentScreenStepper[] = [
  {
    index: 1,
    title: 'Orders',
    screen: 'orders',
  },
  {
    index: 2,
    title: 'Program',
    screen: 'recommended-program',
  },
  {
    index: 3,
    title: 'Note',
    screen: 'note',
  },
  {
    index: 4,
    title: 'Review',
    screen: 'review',
  },
]
